/**
 * Request Queue Management
 * Implements transparent retry mechanism for DVA effects during token refresh
 * This keeps retry completely invisible to DVA layer
 */

import env from '@/config/env';
import retryTracker from './retryTracker';

let requestQueue = [];
let isProcessingQueue = false;
let queueMetrics = {
  totalQueued: 0,
  successfulProcessed: 0,
  failedProcessed: 0,
  timeouts: 0,
};

// Trigger token refresh when first 401 is queued
const triggerTokenRefresh = async () => {
  const { handleAuthError } = require('./authUtils');
  const { getToken } = require('./tokenManager');

  const token = getToken();
  if (!token) throw new Error('No token available for refresh');

  const mockResponse = { status: 401, url: 'mock://token-refresh-trigger' };
  const mockController = new AbortController();
  await handleAuthError(mockResponse, mockController);
};

export const MAX_QUEUE_SIZE = 50;
export const QUEUE_TIMEOUT = 30_000;

/**
 * Add request to queue during token refresh
 * Creates a promise that waits for token refresh completion, then retries the request
 * This makes the retry transparent to DVA - the original request promise resolves with the final result
 * @param {string} url - Request URL
 * @param {object} options - Request options
 * @param {Response} originalResponse - Original 401 response to preserve for errorHandler
 * @returns {Promise} - Promise that resolves when request is retried
 */
export const queueRequest = (url, options, originalResponse) => {
  return new Promise((resolve, reject) => {
    if (requestQueue.length >= MAX_QUEUE_SIZE) {
      queueMetrics.failedProcessed++;
      reject(new Error('Request queue full'));
      return;
    }

    // Get or create tracking key
    let trackingKey = options?.trackingKey;

    if (!trackingKey) {
      trackingKey = retryTracker.generateKey(url, options);
      retryTracker.track(trackingKey, false);
    }

    // Track this as a retry attempt
    const retryCount = retryTracker.track(trackingKey);
    const retryEntry = retryTracker.getEntry(trackingKey);

    if (retryTracker.isMaxReached(trackingKey, env.MAX_RETRY_ATTEMPTS)) {
      retryTracker.markFailed(trackingKey);
      queueMetrics.failedProcessed++;

      reject({
        retryCount,
        response: originalResponse,
        maxRetriesReached: true,
        trackingKey,
      });

      return;
    }

    // Add timeout for queued requests
    const timeoutId = setTimeout(() => {
      const index = requestQueue.findIndex((item) => item.timeoutId === timeoutId);
      if (index !== -1) {
        requestQueue.splice(index, 1);
        retryTracker.markFailed(trackingKey);
        queueMetrics.timeouts++;

        reject({
          retryCount,
          response: originalResponse,
          error: new Error('Request queue timeout'),
          trackingKey,
        });
      }
    }, QUEUE_TIMEOUT);

    queueMetrics.totalQueued++;
    requestQueue.push({
      url,
      options: {
        ...options,
        trackingKey,
        // Preserve Content-Encoding header for gzipped requests on retry
        ...(options?.gzip && { gzip: true }),
      },
      originalResponse,
      resolve,
      reject,
      timeoutId,
      retryCount,
      trackingKey,
      queuedAt: Date.now(),
    });

    // Trigger token refresh if this is the first queued request
    if (requestQueue.length === 1) {
      triggerTokenRefresh().catch(async (error) => {
        await clearRequestQueue(error);
        const { safeLogout } = require('./authUtils');
        await safeLogout(true);
      });
    }
  });
};

/**
 * Clear request queue with error
 * Rejects all queued requests when token refresh fails
 * @param {Error} error - Error to reject all queued requests with
 */
export const clearRequestQueue = async (error) => {
  const requests = [...requestQueue];

  requestQueue = [];
  requests.forEach(({ reject, timeoutId, originalResponse, retryCount, trackingKey }) => {
    if (timeoutId) clearTimeout(timeoutId);
    if (trackingKey) retryTracker.markFailed(trackingKey);

    queueMetrics.failedProcessed++;

    reject({
      response: originalResponse,
      retryCount: retryCount || 1,
      maxRetriesReached: true,
      error,
      trackingKey,
    });
  });
};

export const processRequestQueue = async () => {
  if (isProcessingQueue || requestQueue.length === 0) return;

  const queueSize = requestQueue.length;
  isProcessingQueue = true;

  const requests = [...requestQueue];
  requestQueue = [];

  const { extend } = await import('umi-request');
  const { createRequestInterceptor } = await import('./requestConfig');

  // Lightweight error handler that doesn't recurse into queueing
  const simpleErrorHandler = async (error) => {
    const { response } = error;

    if (response) {
      try {
        const errorData = await response.clone().json();
        throw errorData;
      } catch (pe) {
        throw response;
      }
    }

    throw error;
  };

  // Create temporary request instance for retries without recursive queueing
  const retryAbortController = new AbortController();
  const retryRequest = extend({
    errorHandler: simpleErrorHandler,
    credentials: 'include',
    timeout: 120_000,
  });

  // Use interceptors but prevent recursive queueing during retries
  retryRequest.interceptors.request.use(createRequestInterceptor(retryAbortController));

  // Create response interceptor that doesn't queue (prevents infinite recursion)
  const retryResponseInterceptor = async (response) => {
    if (response.status === 206) return response;
    if (response.status >= 200 && response.status < 300) return response;
    if (response.status === 401) throw { response };
    if (response.status === 403) {
      const { handleAuthError } = await import('./authUtils');
      await handleAuthError(response, retryAbortController);
    }

    throw { response };
  };

  retryRequest.interceptors.response.use(retryResponseInterceptor, { global: false });

  let successCount = 0;
  let failCount = 0;

  for (const {
    url,
    options,
    originalResponse,
    resolve,
    reject,
    timeoutId,
    retryCount,
    trackingKey,
    queuedAt,
  } of requests) {
    if (timeoutId) clearTimeout(timeoutId);
    try {
      const result = await retryRequest(url, options);

      retryTracker.markSuccess(trackingKey);
      queueMetrics.successfulProcessed++;
      successCount++;

      resolve(result);
    } catch (error) {
      const isMaxReached = retryTracker.isMaxReached(trackingKey);
      if (isMaxReached) retryTracker.markFailed(trackingKey);

      queueMetrics.failedProcessed++;
      failCount++;

      reject({
        response: originalResponse,
        retryCount,
        maxRetriesReached: isMaxReached,
        trackingKey,
        error,
      });
    }
  }

  isProcessingQueue = false;

  // Dispatch event for UI updates
  window.dispatchEvent(
    new CustomEvent('queue-processed', {
      detail: {
        total: queueSize,
        success: successCount,
        failed: failCount,
      },
    }),
  );
};

export const getQueueStatus = () => ({
  queueLength: requestQueue.length,
  isProcessing: isProcessingQueue,
  metrics: { ...queueMetrics },
  requests: requestQueue.map((r) => ({
    url: r.url,
    retryCount: r.retryCount,
    queueTime: Date.now() - r.queuedAt,
    trackingKey: r.trackingKey?.substring(0, 30) + '...',
  })),
});

export const resetQueue = () => {
  const requests = [...requestQueue];
  requestQueue = [];

  requests.forEach(({ timeoutId, trackingKey, reject }) => {
    if (timeoutId) clearTimeout(timeoutId);
    if (trackingKey) retryTracker.clear(trackingKey);

    reject({
      error: new Error('Queue reset'),
      retryCount: 0,
      maxRetriesReached: false,
    });
  });

  queueMetrics = {
    totalQueued: 0,
    successfulProcessed: 0,
    failedProcessed: 0,
    timeouts: 0,
  };
};

export const getQueueLength = () => requestQueue.length;
export const isQueueProcessing = () => isProcessingQueue;
export const getQueueMetrics = () => ({ ...queueMetrics });

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__queueStatus = getQueueStatus;
  window.__queueReset = resetQueue;
  window.__queueMetrics = getQueueMetrics;
}
